import { isEqual } from 'lodash'
import type { CSSProperties } from 'react'
import { memo } from 'react'
import { colors } from 'settings/colors'
import type { TColumn, TDataElement } from '../SummaryTable.types'
import { getColumnWidth } from '../utils'

const ValuesBodyRow = ({
  columns,
  row,
  rowIndex,
  onRow,
  rowCSS,
}: {
  columns: TColumn[]
  row: TDataElement
  rowIndex: number
  onRow?: (row: TDataElement, rowIndex: number) => { [key: string]: () => void }
  rowCSS?: (row: TDataElement, rowIndex: number) => CSSProperties
}) => {
  const rowEvents = onRow?.(row, rowIndex) || {}
  const customRowCSS = rowCSS?.(row, rowIndex) || {}

  return (
    <tr
      css={{
        '&:hover': {
          backgroundColor: colors.grey50,
        },
        ...customRowCSS,
      }}
      {...rowEvents}
    >
      {columns.map((col, colIndex) => {
        const isFirstColumn =
          col.key.includes('pointName') || col.key.includes('blockName') || col.key.includes('device')

        return (
          <td
            key={col.key || colIndex}
            css={{
              fontWeight: isFirstColumn ? 'normal' : 'bold',
              padding: '20px 16px',
              position: isFirstColumn ? 'sticky' : 'static',
              left: isFirstColumn ? 0 : 'auto',
              backgroundColor: customRowCSS.backgroundColor || 'white',
              zIndex: isFirstColumn ? 2 : 1,
              minWidth: getColumnWidth(isFirstColumn, col.width),
              maxWidth: getColumnWidth(isFirstColumn, col.width),
              borderBottom: `1px solid ${colors.grey200}`,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              ...(isFirstColumn
                ? {
                    'borderRight': 'none',
                    'boxShadow': "0px 0px 25px 25px rgba(180, 179, 187, 0.7')",
                    '&::after': {
                      boxShadow: "0px 0px 25px 25px rgba(180, 179, 187, 0.7')",
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '1px',
                      height: '100%',
                      backgroundColor: colors.grey200,
                      zIndex: 10,
                    },
                    'borderLeft': 'none',
                  }
                : {
                    borderRight: `1px solid ${colors.grey200}`,
                    borderLeft: 'none',
                  }),
            }}
          >
            {col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]}
          </td>
        )
      })}
    </tr>
  )
}

const MemoizedBodyRow = memo(ValuesBodyRow, isEqual) as typeof ValuesBodyRow

export { MemoizedBodyRow as ValuesBodyRow }
